import { api } from "@/lib/api/client";
import type { FileScanState } from "@altek/ui/models/files/file-scan";
import { waitForScan } from "@/lib/api/file-scan";
import { apiUpload } from "@/lib/api/upload";
import { env } from "@/lib/env";
import type { AttachmentPurpose, UploadIntent } from "@/features/directory/api/directory.types";
import type { LegalEntity } from "./admin.types";

/**
 * Şirket logosu: seç, değiştir, kaldır, çiz.
 *
 * PROFİL FOTOĞRAFIYLA AYNI ÜÇ ADIM, ve bu bilinçli: niyet → PUT → tamamla, sonra sahibi olan
 * modülün kendi ucu bağı kuruyor (`PUT /organization/legal-entities/:id/logo`). İkinci bir
 * yükleme deseni yazmak, amaç/MIME/boyut kurallarının ikinci bir kopyasını doğururdu — arka
 * uçta da aynı gerekçeyle yazılmadı.
 *
 * `directory` ÖZELLİĞİNDEN YALNIZ TİP alınıyor, işlev değil: bir özellik modülü öbürünü
 * çağırmıyor. `UploadIntent` ve `AttachmentPurpose` paylaşılan dosya ucunun şekilleri, bu
 * ekranın icat ettiği bir şey değil; onları burada yeniden yazmak, sunucu birini
 * değiştirdiğinde iki yerden yalnız birinin derleme hatası vermesi olurdu.
 */

const BASE = "/organization/legal-entities";
export const LEGAL_ENTITY_LOGO_PURPOSE = "legal_entity_logo";
export const LEGAL_ENTITY_SUBJECT_TYPE = "organization.legal_entity";

export interface LogoConstraints {
  readonly mimeTypes: readonly string[];
  readonly maxBytes: number;
}

/**
 * Katalog okunamadığında kullanılan sınırlar — `files.purposes.ts`'teki `legal_entity_logo`
 * tanımının aynısı.
 *
 * Yedeği YAZIYA DÖKMEK, katalogsuz bir ekranı sessizce sınırsız bırakmaktan iyi: şirketi
 * yönetebilen ama `files.file.read` taşımayan biri yine çalışan bir denetim alıyor, 25 MB'lık
 * bir görseli yükleyip sunucunun reddini görmüyor.
 */
const FALLBACK: LogoConstraints = {
  mimeTypes: ["image/png", "image/jpeg", "image/webp"],
  maxBytes: 2 * 1024 * 1024,
};

export const legalEntityLogoApi = {
  /** Yüklenmiş ve tamamlanmış bir dosyayı logo olarak bağlıyor; tazelenmiş şirketi döndürüyor. */
  set(id: string, fileId: string): Promise<LegalEntity> {
    return api.put<LegalEntity>(`${BASE}/${encodeURIComponent(id)}/logo`, { fileId });
  },

  remove(id: string): Promise<void> {
    return api.delete(`${BASE}/${encodeURIComponent(id)}/logo`);
  },

  /** Yükleme yuvası kataloğu, bu ekranın öznesine göre süzülmüş. `files.file.read` istiyor. */
  purposes(): Promise<AttachmentPurpose[]> {
    return api.get<AttachmentPurpose[]>("/files/purposes", {
      params: { entityType: LEGAL_ENTITY_SUBJECT_TYPE },
    });
  },

  uploadIntent(input: {
    filename: string;
    contentType: string;
    byteSize: number;
  }): Promise<UploadIntent> {
    return api.post<UploadIntent>("/files/upload-intent", {
      ...input,
      purpose: LEGAL_ENTITY_LOGO_PURPOSE,
      /**
       * `entityId` YOK. Bağı şirket modülü kuruyor (niyet → PUT → tamamla → `PUT …/logo`);
       * kimliği buraya göndermek, dosya servisinden sahibi olmadığı bir bağı kurmasını
       * istemek olurdu — ve o uç yazma politikasını da orada uygular, burada değil.
       */
      entityType: LEGAL_ENTITY_SUBJECT_TYPE,
    });
  },

  completeUpload(fileId: string): Promise<unknown> {
    return api.post(`/files/${encodeURIComponent(fileId)}/complete`, {});
  },
};

/**
 * Şirket logosunun `<img src>`i.
 *
 * LOGONUN GÖSTERİLDİĞİ TEK YOL. İmzalı indirme adresleri `Content-Disposition: attachment`
 * zorluyor ve yerel teslim rotası `application/octet-stream` zorluyor; ikisi de `<img>` içinde
 * çalışmaz. Bu rota oturum çerezi ile kimliklenip `image/*` veriyor.
 *
 * ÇAĞIRAN ÖNCE `hasLogo`'YA BAKMALI: logosu olmayan şirket 404 döndürüyor, ve yirmi şirketlik
 * bir listede kırık görsel simgesi, baş harften kötüdür.
 *
 * `version` şirketin `logoFileId`'si. Yanıt dosyanın özeti ETag'iyle on dakika özel
 * önbellekleniyor, yani değişen bir parametre olmadan tarayıcı değişimden sonra on dakika eski
 * logoyu göstermeye devam ederdi. Sunucu parametreyi yok sayıyor.
 */
export function logoUrlFor(legalEntityId: string, version?: string | null): string {
  const url = `${env.apiBaseUrl}${BASE}/${encodeURIComponent(legalEntityId)}/logo`;
  return version ? `${url}?v=${encodeURIComponent(version)}` : url;
}

/** Denetimin sınırları: katalog okunabiliyorsa onunkiler, okunamıyorsa belgelenmiş olanlar. */
export function logoConstraintsFrom(
  purposes: AttachmentPurpose[] | undefined,
): LogoConstraints {
  /**
   * `Array.isArray`, `purposes?.` değil: `/files/purposes` bu özelliğin sahip olmadığı
   * paylaşılan bir uç ve üretilen belgede 200'ü gövde tanımlamıyor — derleme zamanında dizi
   * olduğunu garanti eden bir şey yok. Bir nesne üzerinde `.find`, seçicinin çiziminde
   * patlayıp Şirketler sayfasını komple düşürürdü.
   */
  const list = Array.isArray(purposes) ? purposes : [];
  const slot = list.find((purpose) => purpose.key === LEGAL_ENTITY_LOGO_PURPOSE);
  if (!slot || slot.mimeTypes.length === 0 || slot.maxBytes <= 0) return FALLBACK;
  return { mimeTypes: slot.mimeTypes, maxBytes: slot.maxBytes };
}

/**
 * Üç adımlı yükleme, baştan sona; şirketin bağlayacağı `fileId`'yi döndürüyor.
 *
 * Baytlar `apiUpload` üzerinden gidiyor: tarayıcının betiğe ayarlatmadığı başlıkları atlıyor
 * ve imzalı adres aynı kaynaktayken CSRF başlığını gönderiyor — ki YEREL depolama sürücüsü tam
 * olarak bu durumda, yani her geliştirici makinesinde.
 *
 * `onProgress` TAM YÜZDE bildiriyor; paylaşılan yardımcının birimi ve seçicinin sakladığı
 * birim o.
 */
export async function uploadLegalEntityLogo(
  file: File,
  onProgress?: (percent: number) => void,
  onScanState?: (state: FileScanState) => void,
): Promise<string> {
  const intent = await legalEntityLogoApi.uploadIntent({
    filename: file.name,
    contentType: file.type,
    byteSize: file.size,
  });

  await apiUpload(
    { url: intent.uploadUrl, method: intent.method, headers: intent.headers },
    file,
    { onProgress },
  );

  /**
   * Tamamlama, dosyayı `CLEAN`'e taşıyan şey. Atlanması, bağlama çağrısının 409
   * `FILE_NOT_READY` ile reddedeceği bir satır bırakıyor — ki bu «logo bozuk» diye okunuyor,
   * oysa gerçek «yükleme hiç bitti denmedi».
   */
  await legalEntityLogoApi.completeUpload(intent.fileId);
  /**
   * TARAMA (K3): `complete` dosyayı AÇMIYOR, taramaya veriyor. Dosya CLEAN olmadan bağlanamıyor,
   * gönderilemiyor, indirilemiyor — bu yüzden akış kararı bekliyor. Ret, sunucunun kendi koduyla
   * (`FILE_INFECTED`, `FILE_ACTIVE_CONTENT`, `FILE_ENCRYPTED`, `FILE_SCAN_FAILED`) bir `ApiError`
   * olarak yukarı çıkıyor ve cümlesini ortak hata kataloğundan alıyor.
   */
  await waitForScan(intent.fileId, onScanState ? { onState: onScanState } : {});
  return intent.fileId;
}
